import React, { type FC } from 'react'

import { useIntl } from 'react-intl'
import { useRecoilCallback, useRecoilValue } from 'recoil'

import {
  BUTTON_VARIANT_PRIMARY,
  BUTTON_VARIANT_SECONDARY,
  Button,
  EmptyState,
  Flex,
  FlexItem,
  LINK_VARIANT_EXTRALIGHT,
  Link,
  Panel,
  SKELETON_LOADER_TYPE_CIRCLE,
  SKELETON_LOADER_VARIANT_ONWHITE,
  SkeletonLoader,
  TEXT_VARIANT_ITALIC_LIGHT,
  TITLE_VARIANT_L,
  Text,
  Title,
} from 'mailup-web-components'

import {
  canBuyEngagementFilters,
  getEngagementDocumentationURL,
  openMshopEngagementFilters,
} from 'Helpers/ecommerce'

import RenderWhen from 'Components/RenderWhen'

import Chart from 'Pages/RecipientsOverview/components/Chart'
import NoChartData from 'Pages/RecipientsOverview/components/NoChartData'

import { modalEngagementUpsellingOpenCallback } from 'Pages/RecipientsOverview/recoil/callbacks/modalEngagementUpselling'
import { loadEngagementCallback } from 'Pages/RecipientsOverview/recoil/callbacks/overview'
import {
  emptyStateState,
  engagementChartState,
} from 'Pages/RecipientsOverview/recoil/state/overview'

const MemoChart = React.memo(Chart)

const BoxEngagement: FC = () => {
  const intl = useIntl()

  const isEmptyState = useRecoilValue(emptyStateState)
  const { upselling, data, status, loading } =
    useRecoilValue(engagementChartState)

  const openModal = useRecoilCallback(modalEngagementUpsellingOpenCallback, [])
  const handleReload = useRecoilCallback(loadEngagementCallback, [])

  const handleLearnMore = (): void => {
    window.open(
      getEngagementDocumentationURL(intl.locale),
      '_blank',
      'noreferrer,noopener',
    )
  }

  const handleBuy = (): void => {
    if (canBuyEngagementFilters()) {
      openMshopEngagementFilters()
    } else {
      void openModal()
    }
  }

  return (
    <Flex flexDirection="column" gap="16px">
      <Title variant={TITLE_VARIANT_L}>
        {intl.formatMessage({
          id: 'Load.RecipientsOverview.EngagementTitle',
        })}
      </Title>

      <Panel margin="0">
        <RenderWhen condition={loading}>
          <Flex gap="64px" justifyContent="center">
            <FlexItem>
              <SkeletonLoader
                type={SKELETON_LOADER_TYPE_CIRCLE}
                variant={SKELETON_LOADER_VARIANT_ONWHITE}
                width="270px"
                height="270px"
                margin="0px"
              />
            </FlexItem>
            <Flex
              flexDirection="column"
              alignItems="flex-start"
              justifyContent="center"
              height="270px"
              width="320px"
            >
              <SkeletonLoader
                variant={SKELETON_LOADER_VARIANT_ONWHITE}
                width="100px"
                height="20px"
                margin="0 0 8px 0"
              />
              <SkeletonLoader
                variant={SKELETON_LOADER_VARIANT_ONWHITE}
                width="100px"
                height="20px"
                margin="0 0 8px 0"
              />
              <SkeletonLoader
                variant={SKELETON_LOADER_VARIANT_ONWHITE}
                width="100px"
                height="20px"
                margin="0 0 8px 0"
              />
            </Flex>
          </Flex>
        </RenderWhen>

        <RenderWhen condition={status === 'error'}>
          <Flex alignItems="center" justifyContent="center">
            <Text variant={TEXT_VARIANT_ITALIC_LIGHT}>
              {intl.formatMessage({ id: 'Load.Common.LoadDataError' })}
            </Text>
            <Link variant={LINK_VARIANT_EXTRALIGHT} onClick={handleReload}>
              {intl.formatMessage({ id: 'Load.Common.RealoadPage' })}
            </Link>
          </Flex>
        </RenderWhen>

        <RenderWhen condition={status === 'ready'}>
          <RenderWhen condition={upselling}>
            <EmptyState
              title={intl.formatMessage({
                id: 'Load.Filters.EngagementUpsellingTitle',
              })}
              description={intl.formatMessage({
                id: 'Load.Filters.EngagementUpsellingDescription',
              })}
            >
              <Flex alignItems="center" gap="16px">
                <Button
                  kind={BUTTON_VARIANT_SECONDARY}
                  data-mu-test="engagement-upselling__learn-more"
                  onClick={handleLearnMore}
                >
                  {intl.formatMessage({
                    id: 'Load.Filters.EngagementUpsellingLearnMore',
                  })}
                </Button>

                <Button
                  kind={BUTTON_VARIANT_PRIMARY}
                  data-mu-test="engagement-upselling__buy"
                  onClick={handleBuy}
                >
                  {intl.formatMessage({
                    id: 'Load.Filters.EngagementUpsellingBuy',
                  })}
                </Button>
              </Flex>
            </EmptyState>
          </RenderWhen>

          <RenderWhen condition={!upselling}>
            <RenderWhen condition={!isEmptyState}>
              <MemoChart id="engagement-chart" data={data} title="" />
            </RenderWhen>
            <RenderWhen condition={isEmptyState}>
              <NoChartData />
            </RenderWhen>
          </RenderWhen>
        </RenderWhen>
      </Panel>
    </Flex>
  )
}

export default BoxEngagement
